import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTeam } from '@/contexts/TeamContext';
import { UsersThree, CheckCircle, WarningCircle, SpinnerGap, ShieldCheck, User, SignIn } from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;
const ROLE_LABEL = { admin: 'Admin', member: 'Member' };

function JoinTeam({ user }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const { fetchTeams, switchTeam } = useTeam();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [invite, setInvite] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(!user);
  const [currentUser, setCurrentUser] = useState(user || null);

  useEffect(() => {
    if (user) { setCurrentUser(user); setCheckingAuth(false); return; }
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setCurrentUser(res.data);
      } catch { /* not logged in */ }
      finally { setCheckingAuth(false); }
    };
    checkAuth();
  }, [user]);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/teams/join/${token}`);
        setInvite(res.data);
        setStatus('ready');
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invite link');
        setStatus('error');
      }
    };
    fetchInvite();
  }, [token]);

  const handleJoin = async () => {
    if (!currentUser) {
      localStorage.setItem('helmcore_return_url', `/join/${token}`);
      navigate('/');
      return;
    }
    setStatus('joining');
    try {
      const res = await axios.post(`${API}/teams/join/${token}`, {}, { withCredentials: true });
      const joinedTeamId = res.data.team_id;

      // Fetch teams and auto-switch to the joined team
      await fetchTeams();
      const teamsRes = await axios.get(`${API}/teams`, { withCredentials: true });
      const joinedTeam = teamsRes.data.find(t => t.team_id === joinedTeamId);
      if (joinedTeam) switchTeam(joinedTeam);

      setStatus('success');

      // Auto-redirect to dashboard after brief welcome
      setTimeout(() => {
        navigate('/dashboard');
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join team');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="mb-8">
        <img src="/helmcore-logo.png" alt="Helm Core" className="h-10 mx-auto" onError={(e) => { e.target.style.display = 'none'; }} />
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-xl p-8 text-center shadow-sm max-w-md w-full">

        {(status === 'loading' || checkingAuth) && (
          <>
            <SpinnerGap size={36} className="text-navy mx-auto mb-4 animate-spin" />
            <p className="text-sm text-slate-500">Loading invite details...</p>
          </>
        )}

        {status === 'ready' && !checkingAuth && invite && (
          <>
            <div className="w-14 h-14 rounded-full bg-navy/10 flex items-center justify-center mx-auto mb-5">
              <UsersThree size={28} weight="duotone" className="text-navy" />
            </div>
            <h1 className="text-xl font-heading font-semibold text-slate-900 mb-1" data-testid="join-team-name">
              Join {invite.team_name}
            </h1>
            <p className="text-sm text-slate-500 mb-5" data-testid="join-inviter-name">
              {invite.inviter_name} invited you to join as
            </p>
            <div className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 mb-6"
              data-testid="join-role-badge">
              {invite.role === 'admin' ? <ShieldCheck size={14} weight="fill" className="text-navy" /> : <User size={14} className="text-slate-500" />}
              <span className="text-sm font-medium text-slate-700">{ROLE_LABEL[invite.role] || invite.role}</span>
            </div>
            {currentUser ? (
              <button onClick={handleJoin}
                className="w-full bg-navy text-white hover:bg-navy-hover font-medium px-4 py-3 rounded-lg text-sm transition-all duration-200 shadow-sm hover:shadow-md"
                data-testid="accept-invite-btn">
                Accept &amp; Join Team
              </button>
            ) : (
              <button onClick={handleJoin}
                className="w-full bg-navy text-white hover:bg-navy-hover font-medium px-4 py-3 rounded-lg text-sm transition-all duration-200 shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                data-testid="login-to-accept-btn">
                <SignIn size={16} weight="bold" />
                Sign in to Accept Invite
              </button>
            )}
            <p className="text-xs text-slate-400 mt-3">Expires {new Date(invite.expires_at).toLocaleDateString()}</p>
          </>
        )}

        {status === 'joining' && (
          <>
            <SpinnerGap size={36} className="text-navy mx-auto mb-4 animate-spin" />
            <p className="text-sm text-slate-500">Joining team...</p>
          </>
        )}

        {status === 'success' && (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto mb-4" />
            <h1 className="text-xl font-heading font-semibold text-slate-900 mb-2" data-testid="join-success-heading">
              You have joined {invite?.team_name}
            </h1>
            <p className="text-sm text-slate-500 mb-2">Taking you to the team workspace...</p>
            <div className="w-32 h-1 bg-slate-100 rounded-full mx-auto overflow-hidden">
              <motion.div className="h-full bg-navy rounded-full" initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 2.5 }} />
            </div>
          </motion.div>
        )}

        {status === 'error' && (
          <>
            <WarningCircle size={40} weight="fill" className="text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-heading font-semibold text-slate-900 mb-2">Unable to Join</h1>
            <p className="text-sm text-red-600 mb-6" data-testid="join-error-msg">{error}</p>
            <button onClick={() => navigate('/')} className="text-sm text-navy font-medium hover:underline"
              data-testid="back-to-dashboard-btn">Back to Home</button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default JoinTeam;
